/**
 * Notes Domain Shared Types
 *
 * Task: T-P5-D9-F3-1 (Foundation)
 *
 * Prisma-agnostic type contracts for the notes domain.
 * Used by the API service layer, webapp frontend, and worker.
 *
 * Prisma enums (NoteCategory, NotePriority, NotePinScope) are NOT importable
 * in the shared package. We follow the tags pattern: define `as const` arrays
 * with derived literal union types.
 */

// ============================================================================
// Enum-like Const Arrays & Derived Types
// ============================================================================

/** Note categories — mirrors Prisma NoteCategory enum */
export const NOTE_CATEGORIES = [
  'GENERAL',
  'OBSERVATION',
  'BACKGROUND',
  'PREFERENCES',
  'FOLLOW_UP',
  'COMMUNICATION',
] as const;
export type NoteCategoryType = (typeof NOTE_CATEGORIES)[number];

/** Note priorities — mirrors Prisma NotePriority enum */
export const NOTE_PRIORITIES = ['CRITICAL', 'HIGH', 'NORMAL'] as const;
export type NotePriorityType = (typeof NOTE_PRIORITIES)[number];

/** Note pin scopes — mirrors Prisma NotePinScope enum */
export const NOTE_PIN_SCOPES = ['HOME', 'CLIENT', 'CALENDAR_EVENT'] as const;
export type NotePinScopeType = (typeof NOTE_PIN_SCOPES)[number];

/** Reminder state for note list surfaces. */
export const NOTE_REMINDER_STATES = ['NONE', 'ACTIVE', 'SENT'] as const;
export type NoteReminderStateType = (typeof NOTE_REMINDER_STATES)[number];

// ============================================================================
// List / Card DTOs
// ============================================================================

/**
 * Shared card DTO for all note list surfaces.
 * Used in Overview, All Notes, client tab, session/event sections, and mentions.
 */
export interface NoteListItemDto {
  id: string;
  contentPreview: string;
  templateName: string | null;
  pinScopes: NotePinScopeType[];
  category: NoteCategoryType;
  priority: NotePriorityType;
  hasActiveReminder: boolean;
  reminderState: NoteReminderStateType;
  reminderDate: string | null;
  createdAt: string;
  updatedAt: string;

  // Resolved minimal relations
  clientId: string | null;
  clientName: string | null;
  calendarEventId: string | null;
  calendarEventLabel: string | null;
  authorName: string | null;
}

/**
 * Home pin board card — extends list item with pin ordering.
 */
export interface HomePinnedNoteDto extends NoteListItemDto {
  pinOrder: number;
}

// ============================================================================
// Detail DTO
// ============================================================================

/**
 * Full note detail with all resolved relations.
 * Returned by GET /api/admin/notes/:id and POST/PATCH.
 */
export interface NoteDetailDto {
  id: string;
  content: unknown;
  contentPlain: string;
  category: NoteCategoryType;
  priority: NotePriorityType;
  reminderDate: string | null;
  reminderSent: boolean;
  templateId: string | null;
  templateName: string | null;
  createdAt: string;
  updatedAt: string;

  // Resolved relations
  author: { id: string; firstName: string | null; lastName: string | null };
  client: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    avatarUrl: string | null;
  } | null;
  calendarEvent: {
    id: string;
    targetType: 'session' | 'event';
    serviceName: string;
    scheduledAt: string;
  } | null;

  // Pin placements
  pins: Array<{
    id: string;
    scope: NotePinScopeType;
    clientId: string | null;
    calendarEventId: string | null;
    pinOrder: number | null;
  }>;

  // Mentions
  mentions: Array<{
    clientId: string;
    firstName: string | null;
    lastName: string | null;
  }>;
}

// ============================================================================
// Composite / Overview DTOs
// ============================================================================

/** Notes Home overview — all three sections in one call. */
export interface NotesOverviewDto {
  reminders: {
    active: NoteListItemDto[];
    sent: NoteListItemDto[];
  };
  pinned: HomePinnedNoteDto[];
  recentNotes: NoteListItemDto[];
}

/** Client notes tab — single-fetch response. */
export interface ClientNotesResponse {
  pinned: NoteListItemDto[];
  notes: {
    items: NoteListItemDto[];
    cursor: string | null;
  };
  mentionedIn: {
    items: NoteListItemDto[];
    cursor: string | null;
    total: number;
  };
  noteCount: number;
  /**
   * Present ONLY when the request carried `search` (F-82). A search spans the
   * client's own notes AND notes that mention them, so the result is one flat
   * list rather than the three sections — which are returned empty in that
   * case, not omitted, so the response shape never varies.
   */
  searchResults?: {
    items: NoteListItemDto[];
    total: number;
  };
}

/** Session/event notes — grouped response. */
export interface CalendarEventNotesResponse {
  sessionNotes: NoteListItemDto[];
  clientNotes: CalendarEventClientGroupDto[];
}

/** Client group within session/event notes. */
export interface CalendarEventClientGroupDto {
  clientId: string;
  clientName: string;
  clientAvatarUrl: string | null;
  notes: NoteListItemDto[];
}

/** Critical attendee notes for banner rendering. */
export interface CriticalAttendeeNotesResponse {
  clients: Array<{
    clientId: string;
    clientName: string;
    noteCount: number;
    notePreview: string;
  }>;
  totalClients: number;
}

// ============================================================================
// Template DTOs
// ============================================================================

/** Template list card. */
export interface NoteTemplateListItemDto {
  id: string;
  name: string;
  sectionCount: number;
  updatedAt: string;
}

/** Template editor data. */
export interface NoteTemplateDetailDto {
  id: string;
  name: string;
  sections: Array<{ heading: string; prompt: string }>;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Session Picker DTOs (T-P5-D9-F4-1)
// ============================================================================

/**
 * Single row returned by GET /api/admin/notes/session-picker.
 * Shared contract with `InviteOptionDto` for `targetType`/`calendarEventId`/`title`/`startsAt`/`endsAt`
 * so Mode A (invite-options) and Mode B (unfiltered) produce compatible items in the frontend picker.
 */
export interface SessionPickerItemDto {
  targetType: 'session' | 'event';
  calendarEventId: string;
  title: string;
  startsAt: string;
  endsAt: string;
  /**
   * Status of the underlying CalendarEvent. CANCELLED is filtered at the
   * query level, so this is always one of DRAFT, CONFIRMED, or PENDING.
   * Rendered via `<EventStatusBadge>` in the picker row.
   */
  status: 'DRAFT' | 'CONFIRMED' | 'PENDING';
  /**
   * Client display name for 1:1 sessions, composed from the booking's client
   * (`firstName lastName`). `null` for shared events and standalone events.
   * Used in the picker row's "with {clientName}" subtitle.
   */
  clientName: string | null;
}

/** Cursor-paginated response for GET /api/admin/notes/session-picker. */
export interface SessionPickerResponse {
  items: SessionPickerItemDto[];
  cursor: string | null;
}

// ============================================================================
// Calendar Event → Clients resolver (Linker Consistency Correction)
// ============================================================================

/**
 * Minimal client DTO used by the client picker's symmetric narrowing branch.
 * When the note editor already has a session/event linked, the client picker
 * fetches this list to narrow the choices to the 1:1 booker (for sessions)
 * or the attendees (for shared events). Enough fields to render a picker
 * row without a second round trip — mirrors the ClientPickerSuggestion shape.
 */
export interface CalendarEventClientDto {
  id: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  /** Telegram @handle — mirrors ClientPickerSuggestion.handle. */
  handle: string | null;
}

/**
 * Response for GET /api/admin/notes/calendar-event-clients/:calendarEventId.
 * Empty list = no narrowing context (e.g., standalone event with no booking
 * or attendees). Frontend interprets empty as "show all clients".
 */
export interface CalendarEventClientsResponse {
  clients: CalendarEventClientDto[];
}
